import { useTranslation } from "react-i18next"

import { Film, Image, Lightbulb, Pencil, Sparkles, Target, Zap } from "@/components/icons"
import { Card, CardContent } from "@/components/ui/card"
import {
  DemoGenerationCompleted,
  DemoGenerationProgress,
  DemoImageEditor,
  DemoImageWizardSteps,
  DemoMediaTypeSelector,
  DemoStyleTemplatePicker,
} from "@/features/training/components/demo"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"

const FEATURES = [
  { icon: Image, key: "create_images" },
  { icon: Film, key: "create_videos" },
  { icon: Pencil, key: "edit_images" },
] as const

const TIPS = [
  { icon: Target, key: "be_specific" },
  { icon: Lightbulb, key: "use_references" },
  { icon: Zap, key: "test_fast" },
  { icon: Sparkles, key: "save_prompts" },
] as const

export function StudioAIGettingStartedPage() {
  const { t } = useTranslation("", { keyPrefix: "training.studio-ai.getting-started" })

  return (
    <CourseLayout>
      {/* Intro */}
      <CourseSection>
        <h1>{t("title")}</h1>
        <p>{t("intro")}</p>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {FEATURES.map(({ icon: Icon, key }) => (
            <Card key={key}>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t(`features.${key}.title`)}</p>
                  <p className="text-muted-foreground text-xs">
                    {t(`features.${key}.description`)}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CourseSection>

      {/* Image Creation */}
      <CourseSection>
        <h2>{t("image_creation.title")}</h2>
        <p>{t("image_creation.description")}</p>

        <div className="my-4">
          <DemoImageWizardSteps />
        </div>

        <div className="bg-muted/30 rounded-xl border p-4">
          <ul className="space-y-2 text-sm">
            {ensureArray<string>(t("image_creation.steps", { returnObjects: true })).map(
              (step, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-primary font-bold">{i + 1}.</span>
                  <span>{step}</span>
                </li>
              )
            )}
          </ul>
        </div>
      </CourseSection>

      {/* Video Creation */}
      <CourseSection>
        <h2>{t("video_creation.title")}</h2>
        <p>{t("video_creation.description")}</p>

        <div className="my-4">
          <DemoMediaTypeSelector />
        </div>

        <div className="my-4 flex flex-col gap-4 sm:flex-row">
          <DemoGenerationProgress />
          <DemoGenerationCompleted />
        </div>

        <ul className="list-inside list-disc space-y-1 text-sm">
          {ensureArray<string>(t("video_creation.options", { returnObjects: true })).map(
            (opt, i) => (
              <li key={i}>{opt}</li>
            )
          )}
        </ul>
      </CourseSection>

      {/* Image Editor */}
      <CourseSection>
        <h2>{t("image_editor.title")}</h2>
        <p>{t("image_editor.description")}</p>

        <div className="my-4">
          <DemoImageEditor />
        </div>

        <ul className="list-inside list-disc space-y-1 text-sm">
          {ensureArray<string>(t("image_editor.use_cases", { returnObjects: true })).map(
            (uc, i) => (
              <li key={i}>{uc}</li>
            )
          )}
        </ul>
      </CourseSection>

      {/* Style Templates */}
      <CourseSection>
        <h2>{t("style_templates.title")}</h2>
        <p>{t("style_templates.description")}</p>

        <div className="my-4">
          <DemoStyleTemplatePicker />
        </div>

        <ul className="list-inside list-disc space-y-1 text-sm">
          {ensureArray<string>(t("style_templates.points", { returnObjects: true })).map(
            (pt, i) => (
              <li key={i}>{pt}</li>
            )
          )}
        </ul>
      </CourseSection>

      {/* Tips */}
      <CourseSection>
        <h2>{t("tips.title")}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {TIPS.map(({ icon: Icon, key }) => (
            <Card key={key}>
              <CardContent className="flex items-start gap-3 p-4">
                <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t(`tips.${key}.title`)}</p>
                  <p className="text-muted-foreground text-xs">{t(`tips.${key}.description`)}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CourseSection>
    </CourseLayout>
  )
}
